import { useCallback, useMemo, useRef, useState } from "react";
import type { AttachmentLabel } from "@/utils/attachmentLabels";
import { isPaymentReceiptLabel } from "@/utils/attachmentLabels";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/library/card";
import UplaodSection from "../../../../../../components/doc/UplaodSection";
import Documents from "../../../../../../components/doc/Documents";
import RequiredDocuments from "../../../../../../components/doc/RequiredDocuments";
import AttachmentFileExplorer, {
  buildFolderMoveOptions,
} from "../../../../../../components/doc/AttachmentFileExplorer";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import DocumentScopeModal, {
  type DocumentScopeChoice,
} from "@/components/doc/DocumentScopeModal";
import { useDocumentScopeMode } from "@/hooks/useDocumentScopeMode";
import { setDocumentScopeMode } from "@/features/settings/api/documentScopePreference";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import { useRequirementsByLeadOrChild } from "@/features/stage/api/useRequirements";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import {
  attachmentFoldersQueryKey,
  useAttachmentFolders,
} from "@/hooks/api/useAttachmentFolders";
import DeleteDocumentModal from "../../../../../../components/doc/DeleteDocumentModal";
import EditDocumentModal from "../../../../../../components/doc/EditDocumentModal";
import DownloadAllDocumentsButton from "../../../../../../components/doc/DownloadAllDocumentsButton";

type DocFilter = "all" | "work" | "payment";

/** The grain filter over the list: everything, the lead's own, or one deal. */
type ScopeFilter = "all" | "lead" | number;

/** Answered grain for one drop; `null` = a lead document. */
type ResolvedScope = { childId: number | null };

export default function Attachment() {
  const queryClient = useQueryClient();
  const { lead, documents, isChildActive, activeData } =
    useActiveDataMutation();
  const { deleteFile, updateFile } = useDocumentMutaion();
  const [docToDelete, setDocToDelete] = useState<any | null>(null);
  const [docToEdit, setDocToEdit] = useState<any | null>(null);
  const [docFilter, setDocFilter] = useState<DocFilter>("all");
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [uploadAttachmentLabel, setUploadAttachmentLabel] =
    useState<AttachmentLabel>("WORKFILE");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all");

  // ---- Lead document or deal document? -------------------------------------
  // Asked once per drop, and ONLY when the lead actually has deals — a lead
  // with none has nothing to choose between, so it uploads exactly as before.
  const { allChildren, currentChildId } = useChildForm();
  const scopeMode = useDocumentScopeMode();

  const deals = useMemo(
    () =>
      (allChildren ?? [])
        .map((c: any) => ({
          id: Number(c?.id),
          name: String(c?.name || "").trim() || `Deal #${c?.id}`,
        }))
        .filter((d: { id: number }) => Number.isFinite(d.id) && d.id > 0),
    [allChildren]
  );

  /** Lead + every deal — the re-filing targets shown on each document row. */
  const dealOptions = useMemo(
    () => [
      { id: null as number | null, label: "Lead document" },
      ...deals.map((d: { id: number; name: string }) => ({
        id: d.id as number | null,
        label: d.name,
      })),
    ],
    [deals]
  );

  // The open prompt and the promise the waiting upload is parked on.
  const [scopePrompt, setScopePrompt] = useState<{
    fileCount: number;
    fileName: string;
  } | null>(null);
  const pendingScopeRef = useRef<((v: ResolvedScope | null) => void) | null>(
    null
  );

  const resolveScope = useCallback(
    async (
      fileCount: number,
      firstFileName: string
    ): Promise<ResolvedScope | null> => {
      // No deals → nothing to ask. Unchanged behaviour.
      if (deals.length === 0) return { childId: null };
      if (scopeMode === "lead") return { childId: null };
      if (scopeMode === "deal") {
        const openId = Number(currentChildId);
        // "Always the open deal" can only answer while one IS open — on the
        // lead's own slot there is no deal to mean, so we ask rather than guess.
        if (
          Number.isFinite(openId) &&
          deals.some((d: { id: number }) => d.id === openId)
        ) {
          return { childId: openId };
        }
      }

      // Park the upload until the prompt is answered.
      return new Promise<ResolvedScope | null>((resolve) => {
        pendingScopeRef.current = resolve;
        setScopePrompt({ fileCount, fileName: firstFileName });
      });
    },
    [deals, scopeMode, currentChildId]
  );

  const handleScopeConfirm = (choice: DocumentScopeChoice) => {
    if (choice.remember) {
      // Fire-and-forget: the save is best-effort and the answer below must
      // not wait on the network.
      void setDocumentScopeMode(choice.childId === null ? "lead" : "deal");
    }
    pendingScopeRef.current?.({ childId: choice.childId });
    pendingScopeRef.current = null;
    setScopePrompt(null);
  };

  const handleScopeCancel = () => {
    pendingScopeRef.current?.(null);
    pendingScopeRef.current = null;
    setScopePrompt(null);
  };

  const attachProps = useMemo(() => {
    if (!lead) {
      return { attachedTo: "", attachedScope: "", category: "" };
    }
    if (isChildActive && activeData?.id != null) {
      return {
        attachedTo: "child",
        attachedScope: String(activeData.id),
        category: activeData?.stage?.name ?? lead.stage.name,
      };
    }
    return {
      attachedTo: lead.modelName,
      attachedScope: String(lead.id),
      category: lead.stage.name,
    };
  }, [isChildActive, activeData, lead]);

  const { data: attachmentFolders = [] } = useAttachmentFolders(
    attachProps.attachedTo,
    attachProps.attachedScope,
    !!lead
  );

  const moveOptions = useMemo(
    () => buildFolderMoveOptions(attachmentFolders),
    [attachmentFolders]
  );

  const { requirements } = useRequirementsByLeadOrChild(
    activeData?.id ?? 0,
    isChildActive ? "child" : "lead"
  );

  const docRequirements = useMemo(() => {
    if (!requirements || typeof requirements !== "object") return [];
    const docReqs = (requirements as any)?.Documents;
    if (!Array.isArray(docReqs)) return [];
    // Pipeline order: by stage, then creation order — so a long info
    // checklist (seeded stage by stage) reads top-to-bottom like the journey.
    return [...docReqs].sort(
      (a: any, b: any) => (a.stageId ?? 0) - (b.stageId ?? 0) || a.id - b.id
    );
  }, [requirements]);

  const requiredDocNames = useMemo(
    () => docRequirements.map((r: any) => r.referenceKey),
    [docRequirements]
  );

  const filteredDocuments = useMemo(() => {
    if (!documents?.length) return [];
    let list = documents;
    if (docFilter === "payment") {
      list = list.filter((doc: any) =>
        isPaymentReceiptLabel(doc.attachmentLabel)
      );
    } else if (docFilter === "work") {
      list = list.filter((doc: any) => !isPaymentReceiptLabel(doc.attachmentLabel));
    }
    // Lead-or-deal grain. "Lead" is the person's own paperwork (childId null);
    // a deal id is that application's files only.
    if (scopeFilter === "lead") {
      list = list.filter((doc: any) => doc.childId == null);
    } else if (typeof scopeFilter === "number") {
      list = list.filter((doc: any) => Number(doc.childId) === scopeFilter);
    }
    // "All files" = every document in any folder; a specific folder = only that folder
    if (currentFolderId == null) return list;
    return list.filter(
      (doc: any) => (doc.folderId ?? null) === currentFolderId
    );
  }, [documents, docFilter, currentFolderId, scopeFilter]);

  /** Same scope as the open folder tree (lead vs active child) for correct folder paths in zip. */
  const documentsForZip = useMemo(() => {
    if (!documents?.length) return [];
    const at = attachProps.attachedTo;
    const sc = attachProps.attachedScope;
    if (!at || sc === "") return documents;
    const scoped = documents.filter(
      (d: any) =>
        d.attachedTo != null &&
        String(d.attachedTo).toLowerCase() === String(at).toLowerCase() &&
        Number(d.attachedScope) === Number(sc)
    );
    return scoped.length > 0 ? scoped : documents;
  }, [documents, attachProps.attachedTo, attachProps.attachedScope]);

  const handleDeleteClick = (doc: any) => {
    setDocToDelete(doc);
  };

  const handleEditClick = (doc: any) => {
    setDocToEdit(doc);
  };

  const handleConfirmDelete = () => {
    if (!docToDelete) return;

    deleteFile.mutate(
      { id: docToDelete.id },
      {
        onSuccess: () => {
          setDocToDelete(null);
          queryClient.invalidateQueries({
            queryKey: attachmentFoldersQueryKey(
              attachProps.attachedTo,
              attachProps.attachedScope
            ),
          });
          refetchCurrentLead();
          if (isChildActive) {
            queryClient.invalidateQueries({ queryKey: ["get-child"] });
          }
          queryClient.invalidateQueries({ queryKey: ["childs"] });
        },
      }
    );
  };

  const handleSaveEdit = (
    name: string,
    file: File | null,
    attachmentLabel: AttachmentLabel
  ) => {
    if (!docToEdit) return;

    updateFile.mutate(
      {
        id: docToEdit.id,
        name: name,
        file: file || undefined,
        attachedTo: attachProps.attachedTo,
        attachedScope: attachProps.attachedScope,
        attachmentLabel,
      },
      {
        // useDocumentMutaion.updateFile already invalidates get-leads.
        onSuccess: () => {
          setDocToEdit(null);
        },
      }
    );
  };

  const handleRename = (id: string, newName: string) => {
    updateFile.mutate(
      {
        id,
        name: newName,
        attachedTo: attachProps.attachedTo,
        attachedScope: attachProps.attachedScope,
      }
    );
  };

  const handleMoveToFolder = (docId: string, folderId: number | null) => {
    updateFile.mutate(
      {
        id: docId,
        folderId,
        attachedTo: attachProps.attachedTo,
        attachedScope: attachProps.attachedScope,
      }
    );
  };

  const handleMoveToDeal = (docId: string, childId: number | null) => {
    updateFile.mutate({
      id: docId,
      childId,
      attachedTo: attachProps.attachedTo,
      attachedScope: attachProps.attachedScope,
    });
  };

  const handleAttachmentLabelChange = (
    docId: string,
    label: AttachmentLabel
  ) => {
    updateFile.mutate(
      {
        id: docId,
        attachmentLabel: label,
        attachedTo: attachProps.attachedTo,
        attachedScope: attachProps.attachedScope,
      }
    );
  };

  if (!lead) return null;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-4 px-2 pb-4 pt-2 sm:px-4">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[16px] border-0 bg-transparent py-0 shadow-none ring-0 gap-0">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <AttachmentFileExplorer
            attachedTo={attachProps.attachedTo}
            attachedScope={attachProps.attachedScope}
            currentFolderId={currentFolderId}
            onNavigate={setCurrentFolderId}
            docFilter={docFilter}
            onDocFilterChange={setDocFilter}
            extraFilterControls={
              deals.length > 0 ? (
                <select
                  aria-label="Show lead or deal documents"
                  value={scopeFilter === "all" ? "all" : String(scopeFilter)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setScopeFilter(
                      v === "all" ? "all" : v === "lead" ? "lead" : Number(v)
                    );
                  }}
                  className="h-8 max-w-[11rem] shrink-0 cursor-pointer rounded-lg border border-[#d8ddd9] bg-white px-2 text-[12px] leading-none text-[var(--color-black-9)] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1e302a]/20"
                >
                  <option value="all">Lead &amp; deals</option>
                  <option value="lead">Lead only</option>
                  {deals.map((d: { id: number; name: string }) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.name}
                    </option>
                  ))}
                </select>
              ) : null
            }
            extraHeaderActions={
              <DownloadAllDocumentsButton
                documents={documentsForZip}
                folders={attachmentFolders}
                leadName={lead?.name ?? "Lead"}
              />
            }
          >
            <section className="space-y-2" aria-label="Upload files">
              <p
                role="heading"
                aria-level={3}
                className="m-0 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-black-8)]"
              >
                Upload
              </p>
              <UplaodSection
                // uploadFile's hook already invalidates get-leads/childs.
                onSuccess={() => {}}
                attachmentLabel={uploadAttachmentLabel}
                onAttachmentLabelChange={setUploadAttachmentLabel}
                resolveScope={resolveScope}
                props={{
                  ...attachProps,
                  ...(currentFolderId != null ? { folderId: currentFolderId } : {}),
                }}
              />
              {/* A remembered "lead or deal?" answer skips the prompt silently,
                  so say so where the upload happens and let it be taken back
                  here (same preference as Settings → Manage). */}
              {deals.length > 0 && scopeMode !== "ask" && (
                <p className="m-0 flex flex-wrap items-center gap-x-1.5 text-[11px] leading-[1.5] text-[#697571]">
                  <span>
                    {scopeMode === "lead"
                      ? "Uploads go to the lead without asking."
                      : "Uploads go to the open deal without asking."}
                  </span>
                  <button
                    type="button"
                    onClick={() => void setDocumentScopeMode("ask")}
                    className="cursor-pointer font-medium text-[#156548] underline underline-offset-2 hover:text-[#0f4d37]"
                  >
                    Ask me every time
                  </button>
                </p>
              )}
            </section>

            {docRequirements.length > 0 && (
              <section className="space-y-3 rounded-xl bg-white/50 p-3">
                <RequiredDocuments
                  requiredDocs={docRequirements}
                  uploadedDocs={documents}
                  uploadProps={{
                    ...attachProps,
                    ...(currentFolderId != null
                      ? { folderId: currentFolderId }
                      : {}),
                  }}
                  attachmentLabel={uploadAttachmentLabel}
                  resolveScope={resolveScope}
                  onSuccess={() => {}}
                />
              </section>
            )}

            <section
              className="space-y-2"
              aria-label={
                currentFolderId == null
                  ? "All documents"
                  : "Documents in this folder"
              }
            >
              <p
                role="heading"
                aria-level={3}
                className="m-0 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-black-8)]"
              >
                {currentFolderId == null ? "All documents" : "Documents here"}
              </p>
              <div className="rounded-xl bg-white/50 p-3">
                <Documents
                  documents={filteredDocuments}
                  onDelete={handleDeleteClick}
                  onEdit={handleEditClick}
                  onRename={handleRename}
                  requiredDocNames={requiredDocNames}
                  moveOptions={moveOptions}
                  onMoveToFolder={handleMoveToFolder}
                  onAttachmentLabelChange={handleAttachmentLabelChange}
                  dealOptions={deals.length > 0 ? dealOptions : undefined}
                  onMoveToDeal={deals.length > 0 ? handleMoveToDeal : undefined}
                />
              </div>
            </section>
          </AttachmentFileExplorer>
        </CardContent>
      </Card>

      <DocumentScopeModal
        open={!!scopePrompt}
        fileCount={scopePrompt?.fileCount ?? 1}
        fileName={scopePrompt?.fileName}
        leadName={lead?.name}
        deals={deals}
        defaultDealId={isChildActive ? Number(currentChildId) : null}
        onConfirm={handleScopeConfirm}
        onCancel={handleScopeCancel}
      />

      <DeleteDocumentModal
        isOpen={!!docToDelete}
        onClose={() => setDocToDelete(null)}
        onDelete={handleConfirmDelete}
        documentName={docToDelete?.fileName?.split("/").pop() || ""}
        isPending={deleteFile.isPending}
      />

      <EditDocumentModal
        isOpen={!!docToEdit}
        onClose={() => setDocToEdit(null)}
        onSave={handleSaveEdit}
        doc={docToEdit}
        isPending={updateFile.isPending}
        isRequired={
          docToEdit?.name
            ? requiredDocNames.some(
                (n: string) => n.toLowerCase() === docToEdit.name?.toLowerCase()
              )
            : false
        }
      />
    </div>
  );
}
